/**
 * useIdleSession — Gestion de l'inactivité utilisateur.
 *
 * Logique :
 *   - À chaque activité (mouse / keyboard / scroll / click / touch), on
 *     remet à zéro un timer d'inactivité.
 *   - Quand l'inactivité dépasse `idleMs - warningMs`, on déclenche
 *     `onWarn()` (qui doit ouvrir un modal "votre session va expirer").
 *   - Si l'utilisateur reste inactif jusqu'à `idleMs`, on déclenche
 *     `onLogout()` (déconnexion auto).
 *   - Si `enabled === false` (utilisateur non connecté), tous les timers
 *     sont désactivés.
 *
 * Pourquoi pas un seul timer setTimeout ?
 *   - On veut un comportement robuste si l'onglet est en arrière-plan ou
 *     suspendu : on combine `lastActivityRef` + polling toutes les 5s pour
 *     ne pas dépendre uniquement de setTimeout (que les navigateurs peuvent
 *     ralentir voire suspendre).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_IDLE_MS = 30 * 60 * 1000;       // 30 minutes
const DEFAULT_WARNING_MS = 2 * 60 * 1000;     // 2 minutes
const POLL_INTERVAL_MS = 5 * 1000;            // check every 5 seconds

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export default function useIdleSession({
  enabled = true,
  idleMs = DEFAULT_IDLE_MS,
  warningMs = DEFAULT_WARNING_MS,
  onWarn,
  onLogout,
} = {}) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(warningMs / 1000));
  const lastActivityRef = useRef(Date.now());
  const warningFiredRef = useRef(false);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warning) {
      setWarning(false);
      warningFiredRef.current = false;
    }
  }, [warning]);

  // Listen for user activity to reset timer
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = () => resetActivity();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handler, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handler));
    };
  }, [enabled, resetActivity]);

  // Poll to detect when warning / logout thresholds are crossed
  useEffect(() => {
    if (!enabled) return undefined;
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const threshold = idleMs - warningMs;
      if (elapsed >= idleMs) {
        // Inactivité totale dépassée → logout
        if (typeof onLogout === 'function') onLogout();
        setWarning(false);
        warningFiredRef.current = false;
      } else if (elapsed >= threshold) {
        // Zone de warning
        const remaining = Math.max(0, Math.ceil((idleMs - elapsed) / 1000));
        setSecondsLeft(remaining);
        if (!warningFiredRef.current) {
          warningFiredRef.current = true;
          setWarning(true);
          if (typeof onWarn === 'function') onWarn();
        }
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, idleMs, warningMs, onWarn, onLogout]);

  const stayConnected = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  return { warning, secondsLeft, stayConnected, resetActivity };
}
